import { capturePdfFocusReturnTarget } from "@/domains/documents/components/pdf/usePdfDialogScroll";
import { isIfcFile } from "@/domains/documents/constants/fileTypes";
import { supabase } from "@/integrations/supabase/client";
import type { Document } from "@/types/documents";
import { getContentTypeFromPath } from "@/utils/previewUtils";
import { reportDocumentTableFailure } from "./documentTableFailure";
import { resolveTableDocument } from "./documentTableLookup";
import type { DocumentWithTags } from "./documentTableTypes";

import type { documentTableOptions } from "./documentTableOptions";
import type { useTableBasics } from "./useTableBasics";
import type { useTableDialogState } from "./useTableDialogState";
import type { useTablePreviewState } from "./useTablePreviewState";
type Options = Pick<
  ReturnType<typeof useTablePreviewState>,
  | "previewReturnFocusRef"
  | "setPreviewBlobUrl"
  | "setPreviewBlobData"
  | "setOriginalDwgFilePath"
  | "setPreviewDocument"
  | "setPreviewOpen"
  | "toast"
> &
  Pick<
    ReturnType<typeof documentTableOptions>,
    "onPreviewOverride" | "store" | "documents" | "buildingId"
  > &
  Pick<ReturnType<typeof useTableDialogState>, "versionDocsCache"> &
  Pick<ReturnType<typeof useTableBasics>, "t">;
type PreviewMetadata = Pick<
  DocumentWithTags,
  | "id"
  | "filename"
  | "file_path"
  | "building_id"
  | "content_type"
  | "size"
  | "created_at"
  | "parent_document_id"
  | "version"
  | "created_by_name"
>;

function previewMetadata(doc: PreviewMetadata): Document {
  return {
    id: doc.id,
    filename: doc.filename,
    file_path: doc.file_path,
    building_id: doc.building_id,
    content_type: doc.content_type,
    size: doc.size,
    created_at: doc.created_at,
    parent_document_id: doc.parent_document_id,
    version: doc.version ?? undefined,
    created_by_name: doc.created_by_name ?? undefined,
  };
}

function capturePreviewOpener(options: Options, control?: HTMLElement) {
  const opener = control ?? window.document.activeElement;
  options.previewReturnFocusRef.current =
    opener instanceof HTMLElement ? capturePdfFocusReturnTarget(opener) : null;
}

function openMetadataPreview(options: Options, doc: Document) {
  options.setPreviewBlobUrl(null);
  options.setPreviewBlobData(null);
  options.setOriginalDwgFilePath(null);
  options.setPreviewDocument(doc);
  options.setPreviewOpen(true);
}

function openLoadedPreview(options: Options, doc: Document, blob: Blob) {
  options.setPreviewBlobUrl(URL.createObjectURL(blob));
  options.setPreviewBlobData(blob);
  options.setPreviewDocument(doc);
  options.setOriginalDwgFilePath(null);
  options.setPreviewOpen(true);
}

function storageStatusMessage(error: Record<string, unknown>): string {
  return typeof error.statusCode === "string" || typeof error.statusCode === "number"
    ? `Error ${error.statusCode}`
    : "File not available";
}

function storageErrorMessage(error: unknown): string {
  if (typeof error === "string") return error;
  if (typeof error !== "object" || error === null) return "File not available";
  const value = error as Record<string, unknown>;
  if (typeof value.message === "string" && value.message) return value.message;
  if (typeof value.error === "string" && value.error) return value.error;
  return storageStatusMessage(value);
}

function versionStorageErrorMessage(error: unknown): string {
  if (typeof error !== "object" || error === null) return "File not available";
  const value = error as Record<string, unknown>;
  return typeof value.message === "string" && value.message ? value.message : "File not available";
}

async function downloadPreviewBlob(
  filePath: string,
  contentType: string,
  describe: (error: unknown) => string
): Promise<Blob> {
  const { data, error } = await supabase.storage.from("documents").download(filePath);
  if (error) throw new Error(describe(error));
  return new Blob([data], { type: contentType });
}

function showPreviewFailure(options: Options, error: unknown, operation: string) {
  reportDocumentTableFailure(operation, error);
  options.toast({
    title: options.t.documents.versioning.preview.unavailableTitle,
    description:
      error instanceof Error
        ? error.message
        : options.t.documents.versioning.preview.versionUnavailable,
    variant: "destructive",
  });
}

function versionFilenameOf(filePath: string, filename?: string): string {
  return filename?.trim() || filePath.split("/").pop() || "document";
}

function versionMetadata(
  docId: string,
  filePath: string,
  filename: string,
  buildingId: string,
  size: number
): Document {
  return {
    id: docId,
    filename,
    file_path: filePath,
    building_id: buildingId,
    content_type: getContentTypeFromPath(filePath),
    size,
    created_at: new Date().toISOString(),
    parent_document_id: null,
  };
}

export function createTablePreviewHandlers(options: Options) {
  const handlePreviewDocument = async (docId: string, activatingControl?: HTMLElement) => {
    capturePreviewOpener(options, activatingControl);
    if (options.onPreviewOverride) {
      options.onPreviewOverride(docId);
      return;
    }
    const doc = resolveTableDocument(options, docId);
    if (!doc || !doc.file_path) return;
    // IFC opens its metadata-only 3D prompt without downloading the model.
    if (isIfcFile(doc.filename)) {
      openMetadataPreview(options, previewMetadata(doc));
      return;
    }
    try {
      const filePath = doc.file_path;
      const contentType = doc.content_type;
      const blob = await downloadPreviewBlob(
        filePath,
        contentType || "application/octet-stream",
        storageErrorMessage
      );
      openLoadedPreview(
        options,
        { ...previewMetadata(doc), file_path: filePath, content_type: contentType },
        blob
      );
    } catch (error) {
      showPreviewFailure(options, error, "document_preview");
    }
  };

  const handlePreviewVersionDocument = async (
    docId: string,
    filePath: string,
    versionFilename?: string
  ) => {
    capturePreviewOpener(options);
    try {
      const filename = versionFilenameOf(filePath, versionFilename);
      // Version-history IFC prompts keep the original metadata-only contract.
      if (isIfcFile(filename)) {
        openMetadataPreview(options, versionMetadata(docId, filePath, filename, "", 0));
        return;
      }
      const blob = await downloadPreviewBlob(
        filePath,
        getContentTypeFromPath(filePath),
        versionStorageErrorMessage
      );
      openLoadedPreview(
        options,
        versionMetadata(docId, filePath, filename, options.buildingId ?? "", blob.size),
        blob
      );
    } catch (error) {
      showPreviewFailure(options, error, "version_preview");
    }
  };
  return { handlePreviewDocument, handlePreviewVersionDocument };
}
